import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { hospitalApi } from '../api';
import type { HospitalFormData, HospitalListFilters, HospitalBonusFormData } from '../types';

const KEYS = {
  all: ['hospitals'] as const,
  lists: () => [...KEYS.all, 'list'] as const,
  list: (filters: HospitalListFilters) => [...KEYS.lists(), filters] as const,
  detail: (id: string) => [...KEYS.all, 'detail', id] as const,
  my: () => [...KEYS.all, 'my'] as const,
  map: () => [...KEYS.all, 'map'] as const,
  therapists: (id: string) => [...KEYS.all, 'therapists', id] as const,
  bonuses: (id: string) => [...KEYS.all, 'bonuses', id] as const,
};

export function useHospitals(filters: HospitalListFilters = {}) {
  return useQuery({
    queryKey: KEYS.list(filters),
    queryFn: async () => {
      const { data } = await hospitalApi.list(filters);
      return data;
    },
  });
}

export function useHospitalDetail(id: string | undefined) {
  return useQuery({
    queryKey: KEYS.detail(id!),
    queryFn: async () => {
      const { data } = await hospitalApi.getById(id!);
      return data.hospital;
    },
    enabled: !!id,
  });
}

export function useMyHospitals() {
  return useQuery({
    queryKey: KEYS.my(),
    queryFn: async () => {
      const { data } = await hospitalApi.listMy();
      return data.hospitals;
    },
  });
}

export function useMapHospitals() {
  return useQuery({
    queryKey: KEYS.map(),
    queryFn: async () => {
      const { data } = await hospitalApi.listMap();
      return data.hospitals;
    },
  });
}

export function useHospitalTherapists(id: string | undefined) {
  return useQuery({
    queryKey: KEYS.therapists(id!),
    queryFn: async () => {
      const { data } = await hospitalApi.getTherapists(id!);
      return data.therapists;
    },
    enabled: !!id,
  });
}

export function useCreateHospital() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (formData: HospitalFormData) => {
      const { data } = await hospitalApi.create(formData);
      return data.hospital;
    },
    onSuccess: () => {
      queryClient.refetchQueries({ queryKey: KEYS.all });
    },
  });
}

export function useUpdateHospital() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<HospitalFormData> }) => {
      const { data: response } = await hospitalApi.update(id, data);
      return response.hospital;
    },
    onSuccess: () => {
      queryClient.refetchQueries({ queryKey: KEYS.all });
    },
  });
}

export function useDeactivateHospital() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      await hospitalApi.deactivate(id);
    },
    onSuccess: () => {
      queryClient.refetchQueries({ queryKey: KEYS.all });
    },
  });
}

export function useAssignTherapist() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ hospitalId, therapistId, isPrimary }: { hospitalId: string; therapistId: string; isPrimary?: boolean }) => {
      await hospitalApi.assignTherapist(hospitalId, therapistId, isPrimary);
    },
    onSuccess: () => {
      queryClient.refetchQueries({ queryKey: KEYS.all });
      queryClient.refetchQueries({ queryKey: ['users'] });
    },
  });
}

export function useUnassignTherapist() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ hospitalId, therapistId }: { hospitalId: string; therapistId: string }) => {
      await hospitalApi.unassignTherapist(hospitalId, therapistId);
    },
    onSuccess: () => {
      queryClient.refetchQueries({ queryKey: KEYS.all });
      queryClient.refetchQueries({ queryKey: ['users'] });
    },
  });
}

export function useHospitalBonuses(hospitalId: string | undefined) {
  return useQuery({
    queryKey: KEYS.bonuses(hospitalId!),
    queryFn: async () => {
      const { data } = await hospitalApi.getHospitalBonuses(hospitalId!);
      return data.bonuses;
    },
    enabled: !!hospitalId,
  });
}

export function useCreateHospitalBonus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ hospitalId, data }: { hospitalId: string; data: HospitalBonusFormData }) => {
      const { data: result } = await hospitalApi.createHospitalBonus(hospitalId, data);
      return result.bonus;
    },
    onSuccess: () => {
      queryClient.refetchQueries({ queryKey: KEYS.all });
    },
  });
}

export function useUpdateHospitalBonus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ hospitalId, bonusId, data }: { hospitalId: string; bonusId: string; data: { bonusAmount: number } }) => {
      const { data: result } = await hospitalApi.updateHospitalBonus(hospitalId, bonusId, data);
      return result.bonus;
    },
    onSuccess: () => {
      queryClient.refetchQueries({ queryKey: KEYS.all });
    },
  });
}

export function useDeleteHospitalBonus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ hospitalId, bonusId }: { hospitalId: string; bonusId: string }) => {
      await hospitalApi.deleteHospitalBonus(hospitalId, bonusId);
    },
    onSuccess: () => {
      queryClient.refetchQueries({ queryKey: KEYS.all });
    },
  });
}
